import React, { useState } from "react";
import {
  Box,
  Typography,
  Modal,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { getIcon } from "./utils/assetLoader";

const useStyles = makeStyles((theme) => ({
  headingSection: {
    display: "flex",
    padding: "20px 20px 0px",
    justifyContent: "space-between",
  },
  headingIcon: {
    height: "36px",
    width: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "12px",
    borderRadius: "50%",
    zIndex: -1,
  },
  headingTitle: {
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: "20px",
    display: "flex",
    alignItems: "center",
  },
  headingWrapper: {
    display: "flex",
  },
  headingCloseIcon: {
    cursor: "pointer",
  },
  modalContent: {
    margin: "28px 20px 28px",
    maxHeight: "calc(100vh - 200px)",
  },
  modalButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "16px",
  },
  modalFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "12px 20px 12px",
  },
  backdrop: {
    backdropFilter: "blur(2px)",
    backgroundColor: "rgba(49, 62, 79, 0.44)",
  },
}));

const CommonModal = ({
  open,
  handleClose,
  title,
  onSubmit,
  icon,
  cancelButtonName,
  sendButtonName,
  children,
  isDeleting,
  width,
  height,
  showFooter = true,
  isLoading = false,
  additionalFooterActions,
  type = "info", // info, loading, success, error, warning
  sx, // ✅ Added sx prop
  PaperProps, // ✅ Added PaperProps
  BackdropProps, // ✅ Added BackdropProps
}) => {
  const classes = useStyles();
  const theme = useTheme();
  /* Removed isMobile usage in favor of sx breakpoints */

  // Enhanced icon background color based on modal type
  const getHeadingIconBgColor = () => {
    switch (type) {
      case "loading":
        return "#E3F2FD"; // Light blue for loading
      case "success":
        return "#E8F5E8"; // Light green for success
      case "error":
        return "#FFEBEE"; // Light red for error
      case "warning":
        return "#FFF3E0"; // Light orange for warning
      default:
        return sendButtonName === "Ping" || isDeleting
          ? "#fff0e7"
          : theme.palette.green.light;
    }
  };

  const headingIconBgColor = getHeadingIconBgColor();

  return (
    <Modal
      open={open}
      onClose={type === "loading" ? undefined : handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{ zIndex: 1300, ...sx }} // Ensure high z-index and merge custom sx
      BackdropProps={{
        classes: {
          root: classes.backdrop,
        },
        onClick:
          type === "loading"
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
              }
            : undefined,
        style: type === "loading" ? { pointerEvents: "none" } : undefined,
        ...BackdropProps,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { base: "90%", sm: width ?? "auto" },
          height: { base: "auto", sm: height ?? "auto" },
          maxHeight: "90vh",
          maxWidth: "95vw",
          boxShadow: 24,
          borderRadius: "8px",
          bgcolor: "background.paper",
          outline: "none",
          display: "flex",
          flexDirection: "column",
          ...PaperProps?.sx,
        }}
        onClick={type === "loading" ? (e) => e.stopPropagation() : undefined}
      >
        <Box className={classes.headingSection}>
          <Box className={classes.headingWrapper}>
            <Box
              className={classes.headingIcon}
              style={{ background: headingIconBgColor }}
            >
              {type === "loading" ? (
                <CircularProgress size={20} sx={{ color: "#1976D2" }} />
              ) : (
                <Box
                  width={"20px"}
                  height={"20px"}
                  component={"img"}
                  src={icon ?? getIcon("alert")}
                />
              )}
            </Box>
            <Typography
              className={classes.headingTitle}
              id="modal-modal-title"
              variant="h6"
              component="h2"
              sx={{
                fontWeight: "normal",
                color:
                  type === "error"
                    ? "#d32f2f"
                    : type === "success"
                      ? "#2e7d32"
                      : type === "warning"
                        ? "#ed6c02"
                        : "inherit",
              }}
            >
              {title}
            </Typography>
          </Box>
          {type !== "loading" && (
            <Box
              className={classes.headingCloseIcon}
              component={"img"}
              onClick={handleClose}
              src={getIcon("x")}
            />
          )}
        </Box>
        <Divider sx={{ mt: 2 }} />

        <Box
          className={classes.modalContent}
          sx={{
            overflowY: "auto",
            flex: 1, // Allow content to shrink/grow
            m: { base: 2, md: "28px 20px 28px" },
          }}
        >
          {children}
        </Box>

        {showFooter && (
          <Box sx={{ mt: "auto" }}>
            <Divider />
            <Box
              className={classes.modalFooter}
              sx={{
                display: "flex",
                flexDirection: {
                  base: "column",
                  xs: "column",
                  sm: "row",
                },
                alignItems: "center",
                justifyContent: "flex-end",
                gap: { base: 1, sm: 2 },
                p: { base: 2, sm: "12px 20px 12px" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { base: "column", xs: "column", sm: "row" },
                  gap: 2,
                  width: { base: "100%", xs: "100%", sm: "auto" },
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  color="orangeButton"
                  disabled={isLoading}
                  sx={{
                    height: "35px",
                    border: "1px solid #FF6812",
                    width: { base: "100%", xs: "100%", sm: "auto" },
                    whiteSpace: "nowrap",
                  }}
                >
                  {cancelButtonName ?? "Cancel"}
                </Button>
                <Button
                  onClick={(event) => {
                    event.preventDefault();
                    onSubmit();
                  }}
                  variant="contained"
                  color="greenButton"
                  disabled={isLoading}
                  sx={{
                    color: "white",
                    height: "35px",
                    width: { base: "100%", xs: "100%", sm: "auto" },
                    minWidth: { base: "100%", xs: "100%", sm: "fit-content" },
                    whiteSpace: "nowrap",
                  }}
                >
                  {isLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CircularProgress
                        size={20}
                        color="inherit"
                        sx={{ mr: 1 }}
                      />
                      Loading...
                    </Box>
                  ) : (
                    (sendButtonName ?? "Add")
                  )}
                </Button>
              </Box>
              {additionalFooterActions && <Box>{additionalFooterActions}</Box>}
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default CommonModal;
